import { AxiosInstance, AxiosRequestConfig } from 'axios';
import HttpService, { HttpError } from './HttpService';
import { MICROSOFT_GRAPH_SCOPES, MicrosoftGraphScope } from './interceptors/microsoftGraphInterceptors';

export type MicrosoftHttpError = HttpError & {
  code?: string; // Microsoft Graph error code
  details?: any; // Microsoft Graph error details
};

/**
 * HttpService specifically configured for Microsoft Graph API endpoints
 */
export default class MicrosoftHttpService extends HttpService {
  constructor(scopes: MicrosoftGraphScope[] = [MICROSOFT_GRAPH_SCOPES.USER_READ], axiosInstance?: AxiosInstance) {
    // lazy require to avoid importing ESM-axios during Jest collection
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { createMicrosoftGraphAxios } = require('./interceptors/microsoftGraphInterceptors');

    const baseConfig: AxiosRequestConfig = {
      baseURL: 'https://graph.microsoft.com/v1.0',
      timeout: 30000, // Microsoft Graph can be slower
    };

    const instance = axiosInstance || createMicrosoftGraphAxios(baseConfig, scopes);
    super(instance, baseConfig);
  }

  /**
   * Get Microsoft Graph user profile
   */
  async getUserProfile(): Promise<any> {
    return this.get('/me');
  }

  /**
   * Get user's contacts from Microsoft Graph
   */
  async getContacts(params?: { $top?: number; $skip?: number }): Promise<any> {
    return this.get('/me/contacts', { params });
  }

  /**
   * Get user's calendar events
   */
  async getCalendarEvents(params?: {
    $top?: number;
    $skip?: number;
    startDateTime?: string;
    endDateTime?: string;
  }): Promise<any> {
    return this.get('/me/events', { params });
  }

  /**
   * Get user's emails
   */
  async getEmails(params?: { $top?: number; $skip?: number }): Promise<any> {
    return this.get('/me/messages', { params });
  }

  /**
   * Create a contact in Microsoft Graph
   */
  async createContact(contactData: any): Promise<any> {
    return this.post('/me/contacts', contactData);
  }

  /**
   * Update a contact in Microsoft Graph
   */
  async updateContact(contactId: string, contactData: any): Promise<any> {
    return this.patch(`/me/contacts/${contactId}`, contactData);
  }

  /**
   * Delete a contact from Microsoft Graph
   */
  async deleteContact(contactId: string): Promise<any> {
    return this.delete(`/me/contacts/${contactId}`);
  }

  /**
   * Get SharePoint drive root children
   */
  async getSharePointDriveRoot(siteId: string, driveId: string): Promise<any> {
    return this.get(`/sites/${siteId}/drives/${driveId}/root/children`);
  }

  /**
   * Get OneDrive root children for a specific folder
   */
  async getOneDriveRootChildren(folderPath: string): Promise<any> {
    return this.get(`/me/drive/root:/${folderPath}:/children`);
  }

  /**
   * Get folder children by folder ID
   */
  async getFolderChildren(folderId: string): Promise<any> {
    return this.get(`/me/drive/items/${folderId}/children`);
  }

  /**
   * Get SharePoint folder children
   */
  async getSharePointFolderChildren(siteId: string, driveId: string, folderId: string): Promise<any> {
    return this.get(`/sites/${siteId}/drives/${driveId}/items/${folderId}/children`);
  }

  /**
   * Create a folder in OneDrive
   */
  async createOneDriveFolder(parentFolderId: string, folderName: string): Promise<any> {
    return this.post(`/me/drive/items/${parentFolderId}/children`, {
      name: folderName,
      folder: {},
      "@microsoft.graph.conflictBehavior": "rename",
    });
  }

  /**
   * Create a folder in SharePoint
   */
  async createSharePointFolder(siteId: string, driveId: string, parentFolderId: string | null, folderName: string): Promise<any> {
    const url = parentFolderId
      ? `/sites/${siteId}/drives/${driveId}/items/${parentFolderId}/children`
      : `/sites/${siteId}/drives/${driveId}/root/children`;

    return this.post(url, {
      name: folderName,
      folder: {},
      "@microsoft.graph.conflictBehavior": "rename",
    });
  }

  /**
   * Upload a file to OneDrive
   */
  async uploadOneDriveFile(parentFolderId: string, fileName: string, fileContent: any): Promise<any> {
    return this.put(`/me/drive/items/${parentFolderId}:/${fileName}:/content`, fileContent, {
      headers: {
        'Content-Type': 'application/octet-stream',
      },
    });
  }

  /**
   * Upload a file to SharePoint
   */
  async uploadSharePointFile(siteId: string, driveId: string, parentFolderId: string, fileName: string, fileContent: any): Promise<any> {
    return this.put(`/sites/${siteId}/drives/${driveId}/items/${parentFolderId}:/${fileName}:/content`, fileContent, {
      headers: {
        'Content-Type': 'application/octet-stream',
      },
    });
  }

  /**
   * Download a file
   */
  async downloadFile(fileId: string): Promise<any> {
    return this.get(`/me/drive/items/${fileId}/content`, {
      responseType: 'blob',
    });
  }

  /**
   * Get file metadata
   */
  async getFileMetadata(fileId: string): Promise<any> {
    return this.get(`/me/drive/items/${fileId}`);
  }

  /**
   * Get a SharePoint drive item by its path relative to the drive root.
   * Useful for resolving a folder like "TaskDocuments/TASK-123" to its item ID.
   */
  async getSharePointItemByPath(siteId: string, driveId: string, path: string): Promise<any> {
    return this.get(`/sites/${siteId}/drives/${driveId}/root:/${path}:/`);
  }

  /**
   * Copy a SharePoint drive item into a destination folder.
   * Graph API returns 202 Accepted with a monitor URL (async operation).
   */
  async copySharePointItem(
    siteId: string,
    driveId: string,
    itemId: string,
    destinationFolderId: string,
    newName?: string,
  ): Promise<any> {
    const body: Record<string, any> = {
      parentReference: { driveId, id: destinationFolderId },
    };
    if (newName) body.name = newName;

    return this.post(
      `/sites/${siteId}/drives/${driveId}/items/${itemId}/copy`,
      body,
    );
  }

  /**
   * Delete a SharePoint drive item (file or folder).
   */
  async deleteSharePointItem(siteId: string, driveId: string, itemId: string): Promise<any> {
    return this.delete(`/sites/${siteId}/drives/${driveId}/items/${itemId}`);
  }
}
